// «سعود» — runs what the assistant returned, in the browser, on the page's own data.
// The AI never writes the data: it returns a small action ({type:'excel'|'pdf'|'image'|'chart'|'open_page', …})
// and the files are built here from the rows read by snapshot.js.
//   Excel  → xlsx (RTL sheet, a title row)
//   PDF    → an RTL HTML template rendered off-screen → html2canvas → jsPDF, page by page (rows never split).
//            Arabic is rasterized, never written as jsPDF text (that breaks letter joining).
//   image  → the same template → html2canvas → PNG
//   chart  → data for apexcharts (the modal lives in SaudPanel.vue)
// Heavy libraries load only when an action runs.
// Every file maker resolves with { ok, message, file?: { kind: 'excel'|'pdf'|'image', blob, name } } — the file
// that was downloaded, so SaudPanel.vue can keep a copy for the admin («المحادثات», saud-api.js upload).

export var BUTTONS = { excel: 'تحميل Excel', pdf: 'تحميل PDF', image: 'حفظ الصورة', chart: 'عرض الرسم', open_page: 'افتح الصفحة' };
export var KINDS = { excel: 'ملف Excel', pdf: 'ملف PDF', image: 'صورة', chart: 'رسم بياني', open_page: 'صفحة' };

var DEFAULT_TEMPLATES = {
  logo: 'school', primary: '#146C43', accent: '#F59E0B', paper: 'A4', header: '',
  footer: 'التحضير الذكي — tahdir.net', font_size: 12, show_dates: true,
};

var NO_TABLE = 'الجدول المطلوب لم يعد ظاهراً في الصفحة. افتح الصفحة التي سألت عنها واطلب مرة أخرى.';
var NO_COLUMN = 'لم أجد العمود المطلوب في هذا الجدول. جرّب أن تذكر اسم العمود كما يظهر في الصفحة.';
var NO_ROWS = 'لا توجد صفوف مطابقة للتصفية المطلوبة.';

/* ---------------------------------------------------------------- helpers --------------------------------------- */

function esc(s){
  return String(s == null ? '' : s).replace(/[&<>"']/g, function(c){ return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
}

function latin(s){
  return String(s == null ? '' : s).replace(/[٠-٩]/g, function(d){ return String(d.charCodeAt(0) - 0x0660); })
    .replace(/[۰-۹]/g, function(d){ return String(d.charCodeAt(0) - 0x06F0); });
}

// Arabic-friendly comparison: digits, alef / taa marbuta / yaa forms, diacritics, tatweel
function norm(s){
  return latin(s).toLowerCase().replace(/[\u064B-\u0652\u0640]/g, '').replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي').replace(/\s+/g, ' ').trim();
}

function toNum(v){
  if (typeof v == 'number') return v;
  var s = latin(v).replace(/[,%\s]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  return parseFloat(s);
}

// «1448/5/9», «2026-10-31», «31/10/2026» → «1448-05-09» (sortable); '' when not a date
function dateKey(s){
  var t = latin(s).trim();
  var m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(t) || /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/.exec(t);
  if (!m) return '';
  var y = m[1].length == 4 ? m[1] : m[3], d = m[1].length == 4 ? m[3] : m[1];
  return y + '-' + ('0' + m[2]).slice(-2) + '-' + ('0' + d).slice(-2) + t.slice(m[0].length);
}

function cmp(a, b){
  var na = toNum(a), nb = toNum(b);
  if (na != null && nb != null) return na - nb;
  if (a === '' && b !== '') return 1;
  if (b === '' && a !== '') return -1;
  var da = dateKey(a), db = dateKey(b);
  if (da && db) return da < db ? -1 : da > db ? 1 : 0;
  return String(a).localeCompare(String(b), 'ar', { numeric: true });
}

export function safeName(s){
  s = String(s || 'سعود').replace(/[\\/:*?"<>|#%\n\r\t]+/g, ' ').replace(/\s+/g, ' ').trim();
  return (s || 'سعود').slice(0, 80);
}

export function templatesOf(t){
  var out = Object.assign({}, DEFAULT_TEMPLATES, t || {});
  var fs = parseInt(out.font_size, 10);
  out.font_size = isNaN(fs) ? 12 : Math.min(18, Math.max(9, fs));
  if (!/^#[0-9a-f]{3,8}$/i.test(out.primary)) out.primary = DEFAULT_TEMPLATES.primary;
  if (!/^#[0-9a-f]{3,8}$/i.test(out.accent)) out.accent = DEFAULT_TEMPLATES.accent;
  return out;
}

function hexRgb(hex){
  hex = String(hex || '').replace('#', '');
  if (hex.length == 3) hex = hex.split('').map(function(c){ return c + c; }).join('');
  var n = parseInt(hex.slice(0, 6), 16);
  if (isNaN(n)) return [20, 108, 67];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function tint(hex, a){
  var c = hexRgb(hex);
  return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')';
}

var COLOR_NAMES = {
  red: '#D92D20', 'أحمر': '#D92D20', 'احمر': '#D92D20', green: '#079455', 'أخضر': '#079455', 'اخضر': '#079455',
  yellow: '#EAB308', 'أصفر': '#EAB308', 'اصفر': '#EAB308', orange: '#F79009', 'برتقالي': '#F79009',
  blue: '#1D4ED8', 'أزرق': '#1D4ED8', 'ازرق': '#1D4ED8', purple: '#7A5AF8', 'بنفسجي': '#7A5AF8',
  gray: '#667085', grey: '#667085', 'رمادي': '#667085',
};

function colorOf(c, fallback){
  c = String(c || '').trim();
  if (/^#[0-9a-f]{3,8}$/i.test(c)) return c;
  return COLOR_NAMES[c.toLowerCase()] || COLOR_NAMES[c] || fallback;
}

export function todayLines(){
  var now = new Date(), g = '', h = '';
  try { g = new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'long', year: 'numeric' }).format(now) + ' م'; } catch (e) { g = now.toISOString().slice(0, 10); }
  try { h = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura-nu-latn', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'long', year: 'numeric' }).format(now); } catch (e) { h = ''; }
  if (h && !/هـ/.test(h)) h += ' هـ';
  return { hijri: h, gregorian: g };
}

function download(blob, name){
  var a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(function(){ URL.revokeObjectURL(a.href); }, 8000);
}

export function downloadDataUrl(url, name){
  fetch(url).then(function(r){ return r.blob(); }).then(function(b){ download(b, name); });
}

/* ---------------------------------------------------------------- rows ------------------------------------------ */

export function findTable(tables, id){
  return (tables || []).filter(function(t){ return t.id == id; })[0] || null;
}

function colByKey(table, key){
  return table.columns.filter(function(c){ return c.key == key; })[0] || null;
}

function matches(v, op, value){
  var a = norm(v);
  var vals = Array.isArray(value) ? value : [value];
  if (op == 'in') return vals.some(function(x){ return a == norm(x); });
  if (op == 'contains') return vals.some(function(x){ return a.indexOf(norm(x)) >= 0; });
  if (op == 'gt' || op == 'lt'){
    var c = cmp(latin(v), latin(vals[0]));
    return op == 'gt' ? c > 0 : c < 0;
  }
  // eq: exact, or the cell's first line (cells can hold «حاضر\n07:12»)
  return vals.some(function(x){ var b = norm(x); return a == b || norm(String(v).split('\n')[0]) == b; });
}

// → { ok, message?, columns:[col], rows:[[v…]], groups:[{name, rows}]|null, groupCol, warn? }
export function prepare(action, table){
  if (!table) return { ok: false, message: NO_TABLE };
  var keys = Array.isArray(action.columns) ? action.columns.filter(Boolean) : [];
  var cols = keys.length ? keys.map(function(k){ return colByKey(table, k); }).filter(Boolean) : table.columns.slice();
  var warn = '';
  if (keys.length && !cols.length) return { ok: false, message: NO_COLUMN };
  if (keys.length && cols.length < keys.length) warn = 'تجاهلت أعمدة غير موجودة في الجدول.';
  var rows = table.rows.slice();

  var f = action.filter;
  if (f && f.key){
    var fc = colByKey(table, f.key);
    if (!fc) return { ok: false, message: NO_COLUMN };
    rows = rows.filter(function(r){ return matches(r[fc.index] || '', f.op || 'eq', f.value); });
    if (!rows.length) return { ok: false, message: NO_ROWS };
  }
  var s = action.sort;
  if (s && s.key){
    var sc = colByKey(table, s.key);
    if (!sc) return { ok: false, message: NO_COLUMN };
    var dir = s.dir == 'desc' ? -1 : 1;
    rows = rows.map(function(r, i){ return [r, i]; }).sort(function(a, b){
      return dir * cmp(a[0][sc.index] || '', b[0][sc.index] || '') || a[1] - b[1];
    }).map(function(x){ return x[0]; });
  }
  var groups = null, gc = null;
  if (action.group_by){
    gc = colByKey(table, action.group_by);
    if (!gc) return { ok: false, message: NO_COLUMN };
    var order = [], map = {};
    rows.forEach(function(r){
      var k = String(r[gc.index] || '').split('\n')[0] || '(بدون)';
      if (!map[k]){ map[k] = []; order.push(k); }
      map[k].push(r);
    });
    if (!s || s.key != action.group_by) order.sort(function(a, b){ return cmp(a, b); });
    else if (s.dir == 'desc') order.sort(function(a, b){ return cmp(b, a); });
    groups = order.map(function(k){ return { name: k, rows: map[k] }; });
    // the grouping column repeats the group header: leave it out of the rows
    if (cols.length > 1) cols = cols.filter(function(c){ return c.key != gc.key; });
  }
  return { ok: true, columns: cols, rows: rows, groups: groups, groupCol: gc, warn: warn };
}

function cellOf(row, col){
  var v = row[col.index];
  return v == null ? '' : String(v);
}

/* ---------------------------------------------------------------- Excel ----------------------------------------- */

function loadXlsx(){
  return import(/* webpackChunkName: "saud-xlsx" */ 'xlsx').then(function(m){ return m.utils ? m : (m.default || m); });
}

export function makeExcel(action, table, ctx){
  var p = prepare(action, table);
  if (!p.ok) return Promise.resolve(p);
  return loadXlsx().then(function(XLSX){
    var title = action.title || table.title || 'تقرير';
    var head = ['م'].concat(p.columns.map(function(c){ return c.label; }));
    var width = head.length;
    var d = todayLines();
    var aoa = [[title]];
    var sub = [ctx && ctx.school && ctx.school.name, d.hijri, d.gregorian].filter(Boolean).join(' — ');
    aoa.push([sub]);
    aoa.push([]);
    aoa.push(head);
    var merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: width - 1 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: width - 1 } }];
    function rowOut(r, i){
      return [i + 1].concat(p.columns.map(function(c){
        var v = cellOf(r, c);
        if (c.type == 'number' && !c.personal){ var n = toNum(v); if (n != null) return n; }
        return v;
      }));
    }
    if (p.groups){
      p.groups.forEach(function(g){
        merges.push({ s: { r: aoa.length, c: 0 }, e: { r: aoa.length, c: width - 1 } });
        aoa.push([(p.groupCol ? p.groupCol.label + ': ' : '') + g.name + ' (' + g.rows.length + ')']);
        g.rows.forEach(function(r, i){ aoa.push(rowOut(r, i)); });
      });
    } else {
      p.rows.forEach(function(r, i){ aoa.push(rowOut(r, i)); });
    }
    var ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;
    ws['!cols'] = head.map(function(h, i){
      var w = String(h).length;
      for (var k = 4; k < Math.min(aoa.length, 400); k++){
        var v = aoa[k][i];
        if (v != null) w = Math.max(w, Math.min(60, String(v).split('\n')[0].length));
      }
      return { wch: Math.max(6, Math.min(60, w + 2)) };
    });
    var wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: true }] };
    XLSX.utils.book_append_sheet(wb, ws, safeName(title).replace(/[[\]*?:/\\]/g, ' ').slice(0, 31) || 'Sheet1');
    var name = safeName(title) + '.xlsx';
    var blob = new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    download(blob, name);
    return { ok: true, message: 'تم تحميل ملف Excel (' + p.rows.length + ' صف).' + (p.warn ? ' ' + p.warn : ''), file: { kind: 'excel', blob: blob, name: name } };
  });
}

/* ---------------------------------------------------------------- template -------------------------------------- */

var PAPER = { A4: [794, 1123], 'A4-landscape': [1123, 794] };
var PT = { A4: [595.28, 841.89], 'A4-landscape': [841.89, 595.28] };
var MARGIN = 34;

function loadImage(src, cors){
  return new Promise(function(resolve){
    if (!src) return resolve(null);
    var img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    img.onload = function(){ resolve(img); };
    img.onerror = function(){ resolve(null); };
    img.src = src;
    setTimeout(function(){ resolve(null); }, 6000);
  });
}

// the logo as a data URL (so html2canvas never meets a cross-origin image); school → tahdir → none
function logoUrl(t, school){
  if (t.logo == 'none') return Promise.resolve('');
  var tahdir = window.location.origin + '/logo.png';
  var first = t.logo == 'school' && school && school.logo_url ? school.logo_url : tahdir;
  function asData(src){
    return loadImage(src, true).then(function(img){
      if (!img) return '';
      try {
        var k = Math.min(1, 240 / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
        var c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(img.naturalWidth * k)); c.height = Math.max(1, Math.round(img.naturalHeight * k));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        return c.toDataURL('image/png');
      } catch (e) { return ''; }
    });
  }
  return asData(first).then(function(u){ return u || (first != tahdir ? asData(tahdir) : ''); });
}

function css(t, W){
  var p = t.primary, a = t.accent, fs = t.font_size;
  return '<style>' +
    '.sd{width:' + W + 'px;background:#fff;color:#1f2937;direction:rtl;text-align:right;font-family:"Noto Kufi Arabic",Tahoma,Arial,sans-serif;font-size:' + fs + 'px;line-height:1.7;box-sizing:border-box}' +
    '.sd *{box-sizing:border-box;font-family:"Noto Kufi Arabic",Tahoma,Arial,sans-serif;letter-spacing:0}' +
    '.sd-page{position:relative;overflow:hidden;padding:' + MARGIN + 'px ' + MARGIN + 'px 0}' +
    '.sd-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding-bottom:10px;border-bottom:3px solid ' + p + '}' +
    '.sd-side{flex:1;min-width:0;font-size:' + Math.max(9, fs - 1) + 'px;line-height:1.75;color:#374151}' +
    '.sd-side b{color:#111827}' +
    '.sd-left{text-align:left}' +
    '.sd-logo{flex:none;width:86px;text-align:center}.sd-logo img{max-width:86px;max-height:70px}' +
    '.sd-title{margin:12px 0 10px;text-align:center}' +
    '.sd-title h1{margin:0;font-size:' + (fs + 7) + 'px;font-weight:800;color:' + p + ';line-height:1.5}' +
    '.sd-title p{margin:2px 0 0;color:#6b7280;font-size:' + fs + 'px}' +
    '.sd-meta{display:inline-block;margin-top:4px;padding:1px 10px;border-radius:20px;background:' + tint(p, 0.08) + ';color:' + p + ';font-size:' + Math.max(9, fs - 2) + 'px}' +
    '.sd-t{width:100%;border-collapse:collapse;table-layout:fixed}' +
    '.sd-t th{background:' + p + ';color:#fff;font-weight:700;padding:6px 6px;border:1px solid ' + p + ';text-align:center;vertical-align:middle;word-wrap:break-word;overflow-wrap:anywhere}' +
    '.sd-t td{padding:5px 6px;border:1px solid #d6dbe1;vertical-align:middle;word-wrap:break-word;overflow-wrap:anywhere}' +
    '.sd-t tr.sd-z td{background:#f6f8f8}' +
    '.sd-t td.sd-n{text-align:center;color:#6b7280;width:38px}' +
    '.sd-t td.sd-c{text-align:center}.sd-t td.sd-nw{white-space:nowrap}' +
    '.sd-t tr.sd-g td{background:' + tint(a, 0.16) + ';color:#111827;font-weight:700;border-color:' + tint(a, 0.45) + '}' +
    '.sd-cards{display:flex;gap:10px;margin-bottom:10px}' +
    '.sd-card{flex:1;min-width:0;border:1px solid #d6dbe1;border-top:4px solid ' + p + ';border-radius:10px;padding:8px 10px;background:#fff}' +
    '.sd-card.sd-empty{border:0;background:transparent}' +
    '.sd-card h3{margin:0 0 4px;font-size:' + (fs + 1) + 'px;color:#111827;font-weight:800}' +
    '.sd-kv{display:flex;justify-content:space-between;gap:8px;border-bottom:1px dashed #e5e7eb;padding:2px 0}' +
    '.sd-kv span{color:#6b7280}.sd-kv b{font-weight:700;text-align:left}' +
    '.sd-gh{margin:6px 0 8px;padding:5px 10px;border-radius:8px;background:' + tint(a, 0.16) + ';font-weight:800}' +
    '.sd-sum{border:1px solid #d6dbe1;border-radius:12px;padding:10px 14px;margin-bottom:12px}' +
    '.sd-sum h3{margin:0 0 8px;font-size:' + (fs + 2) + 'px;color:' + p + ';font-weight:800}' +
    '.sd-bar{display:flex;align-items:center;gap:10px;margin:5px 0}' +
    '.sd-bar .l{width:32%;flex:none;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}' +
    '.sd-bar .b{flex:1;height:16px;background:#eef1f4;border-radius:8px;overflow:hidden}' +
    '.sd-bar .b i{display:block;height:100%;background:' + p + ';border-radius:8px}' +
    '.sd-bar .v{width:70px;flex:none;text-align:left;font-weight:700}' +
    '.sd-kpis{display:flex;gap:10px;margin-bottom:12px}' +
    '.sd-kpi{flex:1;border-radius:12px;padding:10px;text-align:center;background:' + tint(p, 0.07) + ';border:1px solid ' + tint(p, 0.2) + '}' +
    '.sd-kpi b{display:block;font-size:' + (fs + 10) + 'px;color:' + p + ';line-height:1.3}' +
    '.sd-note{margin-top:12px;padding:8px 12px;border-radius:10px;background:' + tint(a, 0.12) + ';border-right:4px solid ' + a + '}' +
    '.sd-foot{position:absolute;left:' + MARGIN + 'px;right:' + MARGIN + 'px;bottom:14px;display:flex;justify-content:space-between;gap:10px;border-top:1px solid #e5e7eb;padding-top:6px;font-size:' + Math.max(8, fs - 3) + 'px;color:#6b7280}' +
    '.sd-foot.sd-static{position:static;margin-top:16px;padding-bottom:14px}' +
    // measuring pass: natural column widths (the pages then reuse them as fixed widths)
    '.m-head,.m-body,.m-note{display:flow-root}.m-body .sd-t{table-layout:auto}' +
    '</style>';
}

function headerHtml(action, ctx, t, count){
  var school = (ctx.school && ctx.school.name) || '';
  var d = todayLines();
  var right = ['المملكة العربية السعودية', window.ministry_name || 'وزارة التعليم', school, t.header].filter(Boolean)
    .map(function(x, i){ return i == 2 ? '<b>' + esc(x) + '</b>' : esc(x); }).join('<br>');
  var left = [];
  if (t.show_dates){
    if (d.hijri) left.push('التاريخ: ' + esc(d.hijri));
    left.push('الموافق: ' + esc(d.gregorian));
  }
  left.push('عدد السجلات: ' + count);
  return '<div class="sd-head"><div class="sd-side">' + right + '</div>' +
    '<div class="sd-logo">' + (ctx.logo ? '<img src="' + ctx.logo + '">' : '') + '</div>' +
    '<div class="sd-side sd-left">' + left.join('<br>') + '</div></div>' +
    '<div class="sd-title"><h1>' + esc(action.title || 'تقرير') + '</h1>' +
    (action.subtitle ? '<p>' + esc(action.subtitle) + '</p>' : '') +
    (ctx.metaLine ? '<span class="sd-meta">' + esc(ctx.metaLine) + '</span>' : '') + '</div>';
}

function footerHtml(t, page, pages, isStatic){
  return '<div class="sd-foot' + (isStatic ? ' sd-static' : '') + '"><span>' + esc(t.footer || '') + '</span>' +
    '<span>أُعدّ بواسطة سعود — المساعد الذكي</span>' +
    (pages ? '<span>صفحة ' + page + ' من ' + pages + '</span>' : '') + '</div>';
}

function highlightOf(action, table){
  var list = (Array.isArray(action.highlight) ? action.highlight : []).map(function(h){
    var c = h && colByKey(table, h.key);
    return c ? { index: c.index, value: h.value, color: colorOf(h.color, '#F59E0B') } : null;
  }).filter(Boolean);
  return function(row){
    for (var i = 0; i < list.length; i++){
      var v = row[list[i].index] || '';
      if (matches(v, 'eq', list[i].value) || matches(v, 'contains', list[i].value)) return list[i].color;
    }
    return '';
  };
}

function cellHtml(v){
  return esc(v).replace(/\n/g, '<br>');
}

// the document as units: a page break can only fall between units (table rows, card rows, summary blocks)
function buildUnits(action, table, p, t, W){
  var kind = action.template == 'cards' || action.template == 'summary' ? action.template : 'table';
  var hl = highlightOf(action, table);
  var units = [];
  var thead = '';
  if (kind == 'table'){
    thead = '<tr><th style="width:40px">م</th>' + p.columns.map(function(c){ return '<th>' + esc(c.label) + '</th>'; }).join('') + '</tr>';
    var n = 0;
    var addRows = function(rows){
      rows.forEach(function(r, i){
        n++;
        var color = hl(r);
        var style = color ? ' style="background:' + tint(color, 0.18) + '"' : '';
        units.push({ html: '<tr class="' + (i % 2 ? 'sd-z' : '') + '">' + '<td class="sd-n"' + style + '>' + n + '</td>' +
          p.columns.map(function(c){
            var cls = c.type == 'number' || c.type == 'date' || c.type == 'time' ? ' class="sd-c sd-nw"' : c.type == 'enum' ? ' class="sd-c"' : '';
            return '<td' + cls + style + '>' + cellHtml(cellOf(r, c)) + '</td>';
          }).join('') + '</tr>' });
      });
    };
    if (p.groups){
      p.groups.forEach(function(g){
        units.push({ group: true, html: '<tr class="sd-g"><td colspan="' + (p.columns.length + 1) + '">' +
          esc((p.groupCol ? p.groupCol.label + ': ' : '') + g.name) + ' — ' + g.rows.length + '</td></tr>' });
        n = 0;
        addRows(g.rows);
      });
    } else addRows(p.rows);
  } else if (kind == 'cards'){
    var per = W > 900 ? 3 : 2;
    var titleCol = p.columns[0];
    var rest = p.columns.slice(1);
    var addCards = function(rows){
      for (var i = 0; i < rows.length; i += per){
        var cards = rows.slice(i, i + per).map(function(r){
          var color = hl(r);
          return '<div class="sd-card"' + (color ? ' style="border-top-color:' + color + ';background:' + tint(color, 0.08) + '"' : '') + '>' +
            '<h3>' + cellHtml(cellOf(r, titleCol)) + '</h3>' +
            rest.map(function(c){ return '<div class="sd-kv"><span>' + esc(c.label) + '</span><b>' + cellHtml(cellOf(r, c)) + '</b></div>'; }).join('') + '</div>';
        });
        while (cards.length < per) cards.push('<div class="sd-card sd-empty"></div>');
        units.push({ html: '<div class="sd-cards">' + cards.join('') + '</div>' });
      }
    };
    if (p.groups){
      p.groups.forEach(function(g){
        units.push({ group: true, html: '<div class="sd-gh">' + esc((p.groupCol ? p.groupCol.label + ': ' : '') + g.name) + ' — ' + g.rows.length + '</div>' });
        addCards(g.rows);
      });
    } else addCards(p.rows);
  } else {
    // summary: counts per group / per enum column, as bars
    var total = p.rows.length;
    var kpis = '<div class="sd-kpis"><div class="sd-kpi"><b>' + total + '</b>إجمالي السجلات</div>';
    var blocks = [];
    var countBy = function(col){
      var m = {}, order = [];
      p.rows.forEach(function(r){ var k = String(r[col.index] || '').split('\n')[0] || '(فارغ)'; if (!(k in m)){ m[k] = 0; order.push(k); } m[k]++; });
      return order.map(function(k){ return [k, m[k]]; }).sort(function(a, b){ return b[1] - a[1]; });
    };
    var cols = [];
    if (p.groupCol) cols.push(p.groupCol);
    p.columns.forEach(function(c){ if (cols.length < 4 && c.type == 'enum' && !c.personal && cols.indexOf(c) < 0) cols.push(c); });
    if (!cols.length) cols = p.columns.filter(function(c){ return !c.personal; }).slice(0, 1);
    cols.forEach(function(c, ci){
      var counts = countBy(c);
      if (ci < 2) kpis += '<div class="sd-kpi"><b>' + counts.length + '</b>' + esc(c.label) + ' (عدد القيم)</div>';
      var max = counts.length ? counts[0][1] : 1;
      var list = counts.slice(0, 18);
      var bars = list.map(function(x){
        var color = hl(p.rows.filter(function(r){ return (String(r[c.index] || '').split('\n')[0] || '(فارغ)') == x[0]; })[0] || []) || '';
        return '<div class="sd-bar"><span class="l">' + esc(x[0]) + '</span><span class="b"><i style="width:' + Math.max(2, Math.round(x[1] * 100 / max)) + '%' + (color ? ';background:' + color : '') + '"></i></span>' +
          '<span class="v">' + x[1] + ' <small style="color:#6b7280">(' + Math.round(x[1] * 100 / Math.max(1, total)) + '%)</small></span></div>';
      }).join('');
      blocks.push('<div class="sd-sum"><h3>حسب ' + esc(c.label) + '</h3>' + bars + (counts.length > list.length ? '<div style="color:#6b7280">و' + (counts.length - list.length) + ' قيم أخرى</div>' : '') + '</div>');
    });
    p.columns.forEach(function(c){
      if (c.type != 'number' || c.personal || blocks.length > 6) return;
      var ns = p.rows.map(function(r){ return toNum(r[c.index]); }).filter(function(x){ return x != null; });
      if (!ns.length) return;
      var sum = ns.reduce(function(a, b){ return a + b; }, 0);
      blocks.push('<div class="sd-sum"><h3>' + esc(c.label) + '</h3><div class="sd-kpis" style="margin:0">' +
        [['المجموع', sum], ['المتوسط', Math.round(sum / ns.length * 100) / 100], ['الأعلى', Math.max.apply(null, ns)], ['الأدنى', Math.min.apply(null, ns)]]
          .map(function(x){ return '<div class="sd-kpi"><b>' + x[1] + '</b>' + x[0] + '</div>'; }).join('') + '</div></div>');
    });
    kpis += '</div>';
    units.push({ html: kpis });
    blocks.forEach(function(b){ units.push({ html: b }); });
  }
  return { kind: kind, units: units, thead: thead };
}

function bodyHtml(doc, units, colgroup){
  var html = units.map(function(u){ return u.html; }).join('');
  if (doc.kind == 'table') return '<table class="sd-t">' + (colgroup || '') + '<thead>' + doc.thead + '</thead><tbody>' + html + '</tbody></table>';
  return html;
}

// html2canvas used to drop the spaces between Arabic words: every word in its own <span>
// (the same fix as public/teacher-card.html)
function wordSpans(root){
  var d = root.ownerDocument || document;
  var w = d.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: function(n){
    var p = n.parentNode && n.parentNode.nodeName;
    return p == 'STYLE' || p == 'SCRIPT' ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
  } });
  var ns = [], n;
  while ((n = w.nextNode())) ns.push(n);
  ns.forEach(function(node){
    var t = node.nodeValue;
    if (!t || !/\S\s+\S/.test(t)) return;
    var f = d.createDocumentFragment();
    t.split(/(\s+)/).forEach(function(part){
      if (!part) return;
      if (/^\s+$/.test(part)) f.appendChild(d.createTextNode(' '));
      else { var s = d.createElement('span'); s.textContent = part; f.appendChild(s); }
    });
    node.parentNode.replaceChild(f, node);
  });
}

// an element in the DOM with its real width but invisible (an element outside the DOM renders blank)
function stage(W){
  var wrap = document.createElement('div');
  wrap.className = 'saud-stage';
  wrap.style.cssText = 'position:fixed;left:0;top:0;width:' + W + 'px;height:0;overflow:hidden;opacity:0;z-index:-1;pointer-events:none';
  document.body.appendChild(wrap);
  return wrap;
}

function loadCanvasLibs(withPdf){
  var h = import(/* webpackChunkName: "saud-render" */ 'html2canvas').then(function(m){ return m.default || m; });
  if (!withPdf) return h.then(function(h2c){ return { h2c: h2c }; });
  var j = import(/* webpackChunkName: "saud-pdf" */ 'jspdf').then(function(m){ return m.jsPDF || (m.default && m.default.jsPDF) || m.default; });
  return Promise.all([h, j]).then(function(x){ return { h2c: x[0], jsPDF: x[1] }; });
}

// a small preview of a picture for the action card (top part)
function thumb(canvas){
  var k = Math.min(1, 360 / canvas.width);
  var th = document.createElement('canvas');
  th.width = Math.round(canvas.width * k); th.height = Math.round(Math.min(canvas.height * k, 520));
  th.getContext('2d').drawImage(canvas, 0, 0, Math.round(canvas.width * k), Math.round(canvas.height * k));
  return th.toDataURL('image/jpeg', 0.8);
}

// one horizontal band of an element (a PDF page of a very long design)
function snapCrop(h2c, el, scale, y, height){
  window.scrollTo(0, 0);
  var r = el.getBoundingClientRect();
  var head = document.head;
  return h2c(el, { scale: scale, backgroundColor: '#ffffff', useCORS: true, logging: false, scrollX: 0, scrollY: 0,
    x: r.left, y: r.top + y, width: r.width, height: height,
    windowWidth: Math.max(document.documentElement.clientWidth, el.offsetWidth),
    ignoreElements: function(n){
      return !(n === head || head.contains(n) || n.tagName == 'LINK' || n.tagName == 'STYLE' || n.contains(el) || el.contains(n));
    },
    onclone: fontsIn });
}

function resized(canvas, k){
  if (k >= 1) return canvas;
  var c = document.createElement('canvas');
  c.width = Math.round(canvas.width * k); c.height = Math.round(canvas.height * k);
  var x = c.getContext('2d');
  x.imageSmoothingQuality = 'high';
  x.drawImage(canvas, 0, 0, c.width, c.height);
  return c;
}

function fontsIn(doc){
  var f = doc && doc.fonts;
  if (!f || !f.load) return Promise.resolve();
  return Promise.all(['400', '700', '800'].map(function(w){ return f.load(w + ' 14px "Noto Kufi Arabic"').catch(function(){}); }))
    .then(function(){ return f.ready; }).catch(function(){});
}

function snap(h2c, el, scale){
  var sx = window.scrollX, sy = window.scrollY;
  window.scrollTo(0, 0);
  // html2canvas clones the whole document before drawing: on a page with a 1 000-row table that took seconds
  // per PDF page. Only the stylesheets (the app's CSS <link> sits in <body> and brings the Arabic font — without
  // it the copy lays words out in another font and they touch) and the path to our element are cloned.
  var head = document.head;
  return h2c(el, { scale: scale, backgroundColor: '#ffffff', useCORS: true, logging: false, scrollX: 0, scrollY: 0,
    windowWidth: Math.max(document.documentElement.clientWidth, el.offsetWidth),
    ignoreElements: function(n){
      return !(n === head || head.contains(n) || n.tagName == 'LINK' || n.tagName == 'STYLE' || n.contains(el) || el.contains(n));
    },
    // the copy lays the words out again: it must use the same font as the drawing, or words touch / overlap
    onclone: fontsIn })
    .then(function(c){ window.scrollTo(sx, sy); return c; }, function(e){ window.scrollTo(sx, sy); throw e; });
}

function metaLine(action, table, p){
  var parts = [];
  if (action.filter && action.filter.key){
    var c = colByKey(table, action.filter.key);
    if (c) parts.push(c.label + ': ' + (Array.isArray(action.filter.value) ? action.filter.value.join('، ') : action.filter.value));
  }
  if (action.sort && action.sort.key){
    var s = colByKey(table, action.sort.key);
    if (s) parts.push('مرتب حسب ' + s.label + (action.sort.dir == 'desc' ? ' (تنازلي)' : ''));
  }
  if (p.groupCol) parts.push('مجمّع حسب ' + p.groupCol.label);
  return parts.join(' · ');
}

// PDF / image. ctx: { school, templates, onProgress(text) }
export function makeDocument(action, table, ctx){
  var p = prepare(action, table);
  if (!p.ok) return Promise.resolve(p);
  var t = templatesOf(ctx.templates);
  var paper = PAPER[t.paper] ? t.paper : 'A4';
  var W = PAPER[paper][0], H = PAPER[paper][1];
  var isPdf = action.type == 'pdf';
  var progress = ctx.onProgress || function(){};
  var rowsCount = p.rows.length;
  // an image of thousands of rows is unreadable (and too tall for a canvas): first rows only
  var IMAGE_MAX = 400;
  var cut = false;
  if (!isPdf && rowsCount > IMAGE_MAX && action.template != 'summary'){
    cut = true;
    if (p.groups){
      var left = IMAGE_MAX;
      p.groups = p.groups.map(function(g){ var r = g.rows.slice(0, Math.max(0, left)); left -= r.length; return { name: g.name, rows: r }; }).filter(function(g){ return g.rows.length; });
    }
    p.rows = p.rows.slice(0, IMAGE_MAX);
  }
  var c2 = Object.assign({}, ctx, { metaLine: metaLine(action, table, p) });
  var wrap = null;
  progress('جاري التجهيز…');
  return Promise.all([loadCanvasLibs(isPdf), logoUrl(t, ctx.school), document.fonts && document.fonts.ready ? document.fonts.ready.catch(function(){}) : null])
    .then(function(res){
      var libs = res[0];
      c2.logo = res[1];
      var doc = buildUnits(action, table, p, t, W);
      var note = action.note ? '<div class="sd-note">' + cellHtml(action.note) + '</div>' : '';
      if (cut) note += '<div class="sd-note">تظهر أول ' + IMAGE_MAX + ' صف من ' + rowsCount + ' — للجدول كاملاً اطلب ملف PDF أو Excel.</div>';
      wrap = stage(W);
      var head = headerHtml(action, c2, t, rowsCount);

      if (!isPdf){
        var el = document.createElement('div');
        el.className = 'sd';
        el.innerHTML = css(t, W) + '<div class="sd-page" style="padding-bottom:0">' + head + '<div class="m-body">' + bodyHtml(doc, doc.units) + '</div>' + note + footerHtml(t, 0, 0, true) + '</div>';
        wrap.appendChild(el);
        wordSpans(el);
        var h = el.offsetHeight;
        // whole-number scales only: at 1.4× / 1.5× Chrome rounds the glyph advances and Arabic words touch
        var scale = h * 2 <= 30000 ? 2 : 1;
        progress('جاري رسم الصورة…');
        return snap(libs.h2c, el, scale).then(function(canvas){
          return new Promise(function(resolve){ canvas.toBlob(function(b){ resolve({ blob: b, canvas: canvas }); }, 'image/png'); });
        }).then(function(x){
          var name = safeName(action.title) + '.png';
          download(x.blob, name);
          return { ok: true, message: 'تم حفظ الصورة (' + p.rows.length + ' صف).' + (cut ? ' تظهر أول ' + IMAGE_MAX + ' صف فقط.' : '') + (p.warn ? ' ' + p.warn : ''),
            preview: thumb(x.canvas), file: { kind: 'image', blob: x.blob, name: name } };
        });
      }

      // PDF: measure everything once in a flowing document, then cut it into pages of whole units
      var m = document.createElement('div');
      m.className = 'sd';
      m.innerHTML = css(t, W) + '<div class="sd-page"><div class="m-head">' + head + '</div><div class="m-body">' + bodyHtml(doc, doc.units) + '</div><div class="m-note">' + note + '</div>' + footerHtml(t, 1, 1, true) + '</div>';
      wrap.appendChild(m);
      wordSpans(m);
      var headH = m.querySelector('.m-head').offsetHeight;
      var noteH = m.querySelector('.m-note').offsetHeight;
      var footH = 44;
      var theadH = 0, colgroup = '';
      var unitEls;
      if (doc.kind == 'table'){
        var tb = m.querySelector('.sd-t');
        theadH = tb.tHead ? tb.tHead.offsetHeight : 0;
        unitEls = Array.prototype.slice.call(tb.tBodies[0] ? tb.tBodies[0].rows : []);
        var ths = tb.tHead ? tb.tHead.rows[0].cells : [];
        var total = tb.offsetWidth || 1;
        colgroup = '<colgroup>' + Array.prototype.map.call(ths, function(th){ return '<col style="width:' + (th.offsetWidth * 100 / total).toFixed(3) + '%">'; }).join('') + '</colgroup>';
      } else {
        unitEls = Array.prototype.slice.call(m.querySelector('.m-body').children);
      }
      // a row's height (blocks: with their margins)
      var heights = unitEls.map(function(u){
        var st = getComputedStyle(u);
        var extra = doc.kind == 'table' ? 0 : parseFloat(st.marginTop || 0) + parseFloat(st.marginBottom || 0);
        return Math.ceil(u.getBoundingClientRect().height + extra);
      });
      var inner = H - MARGIN - footH - 10;
      var pages = [], cur = [], used = headH + theadH, first = true;
      for (var i = 0; i < doc.units.length; i++){
        var hgt = heights[i] || 20;
        var nextH = heights[i + 1] || 0;
        var need = hgt + (doc.units[i].group ? nextH : 0);   // a group title never ends a page
        if (cur.length && used + need > inner){
          pages.push(cur); cur = []; first = false; used = theadH + 8;
        }
        cur.push(doc.units[i]); used += hgt;
      }
      var noteOnNew = note && used + noteH > inner;
      pages.push(cur);
      if (noteOnNew) pages.push([]);
      m.remove();

      var pdf = new libs.jsPDF({ orientation: paper == 'A4' ? 'p' : 'l', unit: 'pt', format: 'a4', compress: true });
      var pw = PT[paper][0], ph = PT[paper][1];
      // drawn at 2× (whole-number scale: fractional ones break Arabic word spacing), then resized as an image
      // for long files so they stay reasonable
      var scale = 2, shrink = pages.length > 30 ? 0.65 : pages.length > 10 ? 0.8 : 1;
      var idx = 0;
      function next(){
        if (idx >= pages.length){
          var name = safeName(action.title) + '.pdf';
          var blob = pdf.output('blob');
          download(blob, name);
          return { ok: true, message: 'تم تحميل ملف PDF (' + p.rows.length + ' صف، ' + pages.length + (pages.length == 1 ? ' صفحة' : ' صفحات') + ').' + (p.warn ? ' ' + p.warn : ''),
            file: { kind: 'pdf', blob: blob, name: name } };
        }
        var k = idx;
        progress('جاري تجهيز الصفحة ' + (k + 1) + ' من ' + pages.length + '…');
        var el = document.createElement('div');
        el.className = 'sd';
        var isLast = k == pages.length - 1;
        var body = pages[k].length ? bodyHtml(doc, pages[k], colgroup) : '';
        el.innerHTML = css(t, W) + '<div class="sd-page" style="height:' + H + 'px">' + (k == 0 ? head : '') + body + (isLast ? note : '') + footerHtml(t, k + 1, pages.length) + '</div>';
        wrap.appendChild(el);
        wordSpans(el);
        return snap(libs.h2c, el, scale).then(function(canvas){
          if (k > 0) pdf.addPage('a4', paper == 'A4' ? 'p' : 'l');
          pdf.addImage(resized(canvas, shrink).toDataURL('image/jpeg', 0.85), 'JPEG', 0, 0, pw, ph, undefined, 'FAST');
          el.remove();
          idx++;
          return next();
        });
      }
      return next();
    })
    .then(function(r){ if (wrap) wrap.remove(); return r; }, function(e){
      if (wrap) wrap.remove();
      console.error('[saud] document', e);
      return { ok: false, message: 'تعذّر تجهيز الملف في المتصفح. حاول مرة أخرى أو اطلب ملف Excel.' };
    });
}

/* ---------------------------------------------------------------- chart ----------------------------------------- */

var PALETTE = ['#1D4ED8', '#12B76A', '#F79009', '#F04438', '#0BA5EC', '#7A5AF8', '#98A2B3', '#0E9384', '#EE46BC', '#FDB022'];

// → { ok, options, series, type, title } for vue-apexcharts
export function chartData(action, table, templates){
  if (!table) return { ok: false, message: NO_TABLE };
  var xc = colByKey(table, action.x);
  if (!xc) return { ok: false, message: NO_COLUMN };
  var yc = action.y && action.y != 'count' ? colByKey(table, action.y) : null;
  if (action.y && action.y != 'count' && !yc) return { ok: false, message: NO_COLUMN };
  var p = prepare({ filter: action.filter, columns: [xc.key] }, table);
  if (!p.ok) return p;
  var t = templatesOf(templates);
  var m = {}, order = [];
  p.rows.forEach(function(r){
    // a trailing «.» / «،» sits on the wrong side of a chart label
    var k = String(r[xc.index] || '').split('\n')[0].replace(/[.،,:\s]+$/, '') || '(فارغ)';
    if (!(k in m)){ m[k] = 0; order.push(k); }
    if (yc){ var n = toNum(r[yc.index]); m[k] += n == null ? 0 : n; } else m[k]++;
  });
  if (!order.length) return { ok: false, message: NO_ROWS };
  var type = ['bar', 'pie', 'line'].indexOf(action.chart) >= 0 ? action.chart : 'bar';
  var pairs = order.map(function(k){ return [k, Math.round(m[k] * 100) / 100]; });
  if (type == 'line' || xc.type == 'date' || xc.type == 'time') pairs.sort(function(a, b){ return cmp(a[0], b[0]); });
  else pairs.sort(function(a, b){ return b[1] - a[1]; });
  var limit = type == 'pie' ? 8 : 30;
  if (pairs.length > limit){
    var restSum = pairs.slice(limit - 1).reduce(function(s, x){ return s + x[1]; }, 0);
    pairs = pairs.slice(0, limit - 1).concat(type == 'line' ? [] : [['أخرى', Math.round(restSum * 100) / 100]]);
  }
  var title = action.title || ('حسب ' + xc.label);
  var yName = yc ? yc.label : 'العدد';
  var colors = [t.primary, t.accent].concat(PALETTE);
  var font = '"Noto Kufi Arabic", Tahoma, sans-serif';
  var base = {
    chart: { type: type, fontFamily: font, toolbar: { show: false }, animations: { enabled: false }, background: '#fff' },
    title: { text: title, align: 'center', offsetY: 8, margin: 16, style: { fontSize: '15px', fontWeight: 800, fontFamily: font, color: '#111827' } },
    colors: colors,
    dataLabels: { enabled: type != 'line', style: { fontFamily: font } },
    legend: { position: 'bottom', fontFamily: font },
    tooltip: { style: { fontFamily: font } },
  };
  var series, options;
  if (type == 'pie'){
    series = pairs.map(function(x){ return x[1]; });
    options = Object.assign(base, { labels: pairs.map(function(x){ return x[0]; }) });
  } else {
    var longest = pairs.reduce(function(m, x){ return Math.max(m, String(x[0]).length); }, 0);
    var horizontal = type == 'bar' && (pairs.length > 8 || longest > 10);
    series = [{ name: yName, data: pairs.map(function(x){ return x[1]; }) }];
    options = Object.assign(base, {
      xaxis: { categories: pairs.map(function(x){ return x[0]; }), labels: { style: { fontFamily: font }, trim: true, maxHeight: 120 } },
      // horizontal bars keep their category labels beside the axis (a right-hand axis overlapped the bars)
      yaxis: { labels: { style: { fontFamily: font, fontSize: '12px' }, maxWidth: 260 }, opposite: !horizontal },
      plotOptions: { bar: { horizontal: horizontal, borderRadius: 4, distributed: pairs.length <= 12, dataLabels: { position: horizontal ? 'center' : 'top' } } },
      stroke: { width: type == 'line' ? 3 : 0, curve: 'smooth' },
      markers: { size: type == 'line' ? 4 : 0 },
      grid: { borderColor: '#eef1f4' },
    });
    if (type == 'bar' && pairs.length <= 12) options.legend = { show: false };
  }
  var tall = type == 'bar' && options.plotOptions && options.plotOptions.bar.horizontal;
  return { ok: true, type: type, series: series, options: options, title: title, height: tall ? Math.min(900, Math.max(320, 110 + pairs.length * 42)) : 380 };
}

/* ---------------------------------------------------------------- open_page ------------------------------------- */

export function openPage(action, router){
  var r = String(action.route || '').trim();
  if (!/^\/[^/]/.test(r) && r != '/') return { ok: false, message: 'هذه الصفحة غير متاحة.' };
  if (/^\/_xteacher|^\/\/|^\/(login|logout|register|autologin)/.test(r)) return { ok: false, message: 'هذه الصفحة غير متاحة من هنا.' };
  var resolved = router.resolve(r);
  if (!resolved || !resolved.route || !resolved.route.matched.length || resolved.route.matched[0].path == '*') return { ok: false, message: 'لم أجد هذه الصفحة في النظام.' };
  router.push(r).catch(function(){});
  return { ok: true, message: 'تم فتح الصفحة.' };
}

/* ---------------------------------------------------------------- screenshot ------------------------------------ */

// the visible part of the page content, JPEG ≤ 1280 px wide, quality ~0.6, ≤ 400 KB (only when the school ticks it)
export function pageScreenshot(){
  var el = document.querySelector('.content-body') || document.querySelector('.app-content') || document.body;
  return loadCanvasLibs(false).then(function(libs){
    var r = el.getBoundingClientRect();
    var top = Math.max(0, r.top), bottom = Math.min(window.innerHeight, r.bottom);
    var visH = Math.max(200, bottom - top);
    return libs.h2c(el, {
      scale: 1, backgroundColor: '#ffffff', useCORS: true, logging: false,
      x: r.left + window.scrollX, y: top + window.scrollY, width: r.width, height: Math.min(visH, 2400),
      // table rows below the visible part are not drawn: leaving them out of html2canvas's copy keeps a
      // 1 000-row page fast (rows above stay, or the visible part would move up)
      ignoreElements: function(n){
        if (n.classList && (n.classList.contains('saud-root') || n.classList.contains('saud-stage'))) return true;
        return n.tagName == 'TR' && n.getBoundingClientRect().top > window.innerHeight + 50;
      },
      // in html2canvas's copy only (the page is untouched): the app's letter-spacing makes it draw Arabic letter
      // by letter (unjoined), and words need their own spans to keep their spaces
      onclone: function(doc){
        var st = doc.createElement('style');
        st.textContent = '*{letter-spacing:0 !important}';
        doc.head.appendChild(st);
        try { wordSpans(doc.querySelector('.content-body') || doc.body); } catch (e) { /* best effort */ }
        return fontsIn(doc);
      },
    });
  }).then(function(canvas){
    var c = resized(canvas, Math.min(1, 1280 / Math.max(1, canvas.width)));
    var q = 0.6, url = c.toDataURL('image/jpeg', q);
    while (url.length * 0.75 > 400 * 1024 && (q > 0.35 || c.width > 480)){
      if (q > 0.35) q -= 0.1;
      else {
        var s = document.createElement('canvas');
        s.width = Math.round(c.width * 0.75); s.height = Math.round(c.height * 0.75);
        s.getContext('2d').drawImage(c, 0, 0, s.width, s.height);
        c = s;
      }
      url = c.toDataURL('image/jpeg', q);
    }
    if (url.length * 0.75 > 400 * 1024) throw new Error('too big');
    return url;
  });
}

// shared with design.js
export var internals = {
  PAPER: PAPER, PT: PT, MARGIN: MARGIN, stage: stage, snap: snap, snapCrop: snapCrop, loadCanvasLibs: loadCanvasLibs,
  logoUrl: logoUrl, wordSpans: wordSpans, resized: resized, download: download, thumb: thumb,
};
